import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';

export default function CasesPage() {
  const { cases: cmsCases, siteSettings } = useCms();
  const [cases, setCases] = useState(cmsCases || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (cmsCases && cmsCases.length > 0) {
      setCases(cmsCases);
    }
  }, [cmsCases]);

  return (
    <>
      <Helmet>
        <title>Cases - Ecolaser ApS</title>
        <meta name="description" content="Dyk ned i vores referencecases og se, hvordan ECOLASER løser komplekse opgaver på fredede bygninger, moderne facader og industrianlæg." />
        <meta property="og:title" content="Cases - Ecolaser ApS" />
        <meta property="og:description" content="Dyk ned i vores referencecases og se, hvordan ECOLASER løser komplekse opgaver på fredede bygninger, moderne facader og industrianlæg." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/front-kbh-chr-d-9-high.webp" />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/front-kbh-chr-d-9-high.webp?width=1800&quality=80&format=auto"
            alt="Ecolaser baggrund"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-3">
          <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Referencecases</h1>
          <p className="text-zinc-200 text-sm md:text-base max-w-2xl leading-relaxed">
            Dyk ned i vores udvalgte projekter og se, hvordan vi løser komplekse afrensningsopgaver for både fredet kulturarv, nybyggeri og industri over hele landet.
          </p>
        </div>
      </section>

      {/* Cases Grid Section */}
      <section className="bg-white py-16 min-h-[400px]">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <i className="fa-solid fa-spinner animate-spin text-brand-green text-4xl" />
              <p className="text-zinc-500 font-mono text-xs">Henter referencecases...</p>
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">
              <i className="fa-regular fa-folder-open text-4xl mb-4" />
              <p className="text-sm">Ingen cases fundet i øjeblikket.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cases.map((c) => (
                <div
                  key={c.id}
                  className="bg-zinc-50 border border-zinc-200 hover:border-brand-green/30 rounded-2xl overflow-hidden flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 hover:shadow-sm"
                >
                  <div>
                    <div className="aspect-video w-full overflow-hidden relative">
                      <img
                        src={`${c.image?.[0]}?width=600&quality=80&format=auto`}
                        alt={c.title?.da}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6 flex flex-col gap-3">
                      <h3 className="text-lg font-bold text-zinc-900 leading-snug line-clamp-2">
                        {c.title?.da}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 pt-0">
                    <Link
                      to={`/cases/${c.id}`}
                      className="inline-block w-full text-center bg-brand-green hover:bg-brand-green/90 text-white font-bold py-2.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300"
                    >
                      Læs hele casen
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zinc-900 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-5">
          <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight">
            Har du en lignende opgave?
          </h2>
          <p className="text-zinc-300 text-sm md:text-base max-w-xl leading-relaxed">
            Vi dækker hele Danmark og kommer gerne ud og laver en prøverensning på din overflade.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4 w-full sm:w-auto">
            <a
              href={siteSettings.phoneTel}
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" /> Ring direkte: {siteSettings.phone}
            </a>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-transparent hover:bg-white/10 text-white font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider border border-white transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope" /> Udfyld formular
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
